import { Injectable } from "@angular/core";
import { Message, MessageService } from "primeng/api";

/**
 * MessageService que hace "sticky" (permanente) todo toast de la plataforma.
 *
 * Por defecto PrimeNG descarta los toasts a los 3 segundos, asi que los mensajes
 * de exito/error de una accion del usuario (sincronizar una tabla, guardar un
 * registro, aplicar cambios a graficos, etc.) desaparecian antes de que el
 * usuario pudiera leerlos. Con esta subclase los toasts se quedan en pantalla
 * hasta que el usuario los cierra con la "x".
 *
 * ESCAPE HATCH: un mensaje que si deba autodescartarse (por ejemplo un aviso de
 * "procesando...") puede pasar `sticky: false` explicitamente y conserva el
 * comportamiento temporal de PrimeNG.
 *
 * USO: en lugar de `providers: [MessageService]`, un componente/modulo debe
 * declarar `providers: [STICKY_MESSAGE_SERVICE_PROVIDER]` para que el
 * `<p-toast>` de su plantilla reciba esta implementacion.
 */
@Injectable()
export class StickyMessageService extends MessageService {
    override add(message: Message): void {
        super.add(this.makeSticky(message));
    }

    override addAll(messages: Message[]): void {
        super.addAll(messages.map((message) => this.makeSticky(message)));
    }

    private makeSticky(message: Message): Message {
        return {
            ...message,
            sticky: message.sticky ?? true,
            closable: message.closable ?? true,
        };
    }
}

/**
 * Provider listo para usarse en el arreglo `providers` de un componente o modulo.
 * Mantiene `MessageService` como token de inyeccion para no tocar los
 * constructores existentes ni los `<p-toast>` de las plantillas.
 */
export const STICKY_MESSAGE_SERVICE_PROVIDER = {
    provide: MessageService,
    useClass: StickyMessageService,
};
